/**
 * W-053 — Transaction detail. Stories: US-A12.
 *
 * AC3 the full record, including the provider reference and payload.
 * Priority S / C-priority in docs/05 §17 — first in line if the phase-3 budget
 * runs out — but it is a read-only view over data already fetched, so it is
 * cheap enough to keep.
 */
import { Show } from '@refinedev/antd'
import { useShow } from '@refinedev/core'
import { Card, Descriptions, Typography } from 'antd'
import dayjs from 'dayjs'
import { StatusTag } from '@/components'
import { formatPrice } from '@horizon/i18n'
import { useAdminLocale, useAdminTranslate } from '@/providers/i18n-provider'
import type { Transaction } from '@/types'

export const TransactionShowPage = () => {
  const translate = useAdminTranslate()
  const locale = useAdminLocale()
  const { query } = useShow<Transaction>({ resource: 'transactions' })
  const record = query.data?.data

  return (
    <Show
      isLoading={query.isLoading}
      title={translate('admin.pages.transaction')}
      canDelete={false}
      canEdit={false}
    >
      <Descriptions column={{ xs: 1, md: 2 }} size="small" bordered>
        <Descriptions.Item label={translate('admin.columns.identifier')}>
          {record?.id}
        </Descriptions.Item>
        <Descriptions.Item label={translate('admin.columns.date')}>
          {record ? dayjs(record.created_at).format('DD/MM/YYYY HH:mm') : '—'}
        </Descriptions.Item>
        <Descriptions.Item label={translate('admin.columns.amount')}>
          {record ? formatPrice(record.amount_mru, locale) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label={translate('admin.columns.status')}>
          {record ? <StatusTag kind="transaction" value={record.status} /> : null}
        </Descriptions.Item>
        <Descriptions.Item label={translate('admin.columns.source')}>
          {record?.provider}
        </Descriptions.Item>
        <Descriptions.Item label={translate('admin.columns.providerRef')}>
          {record?.provider_ref ?? '—'}
        </Descriptions.Item>
        <Descriptions.Item label={translate('admin.columns.user')}>
          {record?.user_id}
        </Descriptions.Item>
        <Descriptions.Item label={translate('admin.common.plan')}>
          {record?.plan_id ?? '—'}
        </Descriptions.Item>
      </Descriptions>

      <Card
        size="small"
        title={translate('admin.screens.transactionShow.payload')}
        style={{ marginTop: 16 }}
      >
        <Typography.Paragraph>
          <pre style={{ margin: 0, overflowX: 'auto' }}>
            {JSON.stringify(record?.raw_payload ?? {}, null, 2)}
          </pre>
        </Typography.Paragraph>
      </Card>
    </Show>
  )
}
